import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  LinearProgress,
  Divider,
  Paper,
} from '@mui/material';
import { TrendingUp, TrendingDown, RemoveCircleOutline } from '@mui/icons-material';
import { apiService, MarketPosition as MarketPositionType } from '../services/api';
import { useTheme } from '@mui/material/styles';

const MarketPosition: React.FC = () => {
  const theme = useTheme();
  const [symbol, setSymbol] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [positionData, setPositionData] = useState<MarketPositionType | null>(null);

  const handleAnalyze = async () => {
    if (!symbol) return;

    setLoading(true);
    setError(null);

    try {
      const data = await apiService.getMarketPosition(symbol.toUpperCase());
      setPositionData(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to analyze market position');
    } finally {
      setLoading(false);
    }
  };

  const getSignalColor = (signal: string) => {
    if (signal === 'BUY') return theme.palette.success.main;
    if (signal === 'SELL') return theme.palette.error.main;
    return theme.palette.warning.main;
  };

  const getPositionColor = (position: string) => {
    if (position === 'top') return theme.palette.error.main;
    if (position === 'bottom') return theme.palette.success.main;
    return theme.palette.info.main;
  };

  const getStanceIcon = (stance: string) => {
    if (stance.includes('bottom')) return <TrendingUp />;
    if (stance.includes('top')) return <TrendingDown />;
    return <RemoveCircleOutline />;
  };

  const renderRangeBar = (positionPct: number, label: string) => {
    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {(positionPct * 100).toFixed(1)}% of range
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={positionPct * 100}
          sx={{
            height: 10,
            borderRadius: 5,
            backgroundColor: theme.palette.grey[300],
            '& .MuiLinearProgress-bar': {
              backgroundColor:
                positionPct >= 0.9
                  ? theme.palette.error.main
                  : positionPct <= 0.1
                  ? theme.palette.success.main
                  : theme.palette.info.main,
            },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Bottom (10%)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Middle
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Top (90%)
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Market Position Analysis
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Identify if stocks are at local or global highs/lows relative to market
      </Typography>

      {/* Search Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Stock Symbol"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                onKeyPress={(e) => e.key === 'Enter' && handleAnalyze()}
                placeholder="IONQ"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleAnalyze}
                disabled={loading || !symbol}
                sx={{ height: '56px' }}
              >
                Analyze Position
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Loading / Error */}
      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Position Results */}
      {positionData && !loading && (
        <>
          {/* Current Status */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Typography variant="overline" color="text.secondary">
                    {positionData.symbol} Current Price
                  </Typography>
                  <Typography variant="h3" fontWeight={700}>
                    ${positionData.current_price.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    As of {new Date(positionData.as_of_date).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="overline" color="text.secondary">
                    Overall Signal
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip
                      label={positionData.overall_position.signal}
                      sx={{
                        backgroundColor: getSignalColor(positionData.overall_position.signal),
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                    {getStanceIcon(positionData.overall_position.stance)}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {positionData.overall_position.stance.replace(/_/g, ' ').toUpperCase()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="overline" color="text.secondary">
                    Recommendation
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {positionData.overall_position.recommendation}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Local vs Global Analysis */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Local Position (60 days) */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={600}>
                      Local Position
                    </Typography>
                    <Chip
                      label={positionData.local.position_label}
                      size="small"
                      sx={{
                        backgroundColor: getPositionColor(positionData.local.position),
                        color: 'white',
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    Based on last {positionData.local.range_days} days (~3 months)
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Range High
                      </Typography>
                      <Typography variant="h6" fontWeight={600} color="error.main">
                        ${positionData.local.range_high.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Range Low
                      </Typography>
                      <Typography variant="h6" fontWeight={600} color="success.main">
                        ${positionData.local.range_low.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>

                  {renderRangeBar(positionData.local.position_pct, 'Position in Range')}

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Distance to High
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {positionData.local.distance_to_high_pct.toFixed(1)}%
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Distance to Low
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {positionData.local.distance_to_low_pct.toFixed(1)}%
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Global Position (180 days) */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={600}>
                      Global Position
                    </Typography>
                    <Chip
                      label={positionData.global.position_label}
                      size="small"
                      sx={{
                        backgroundColor: getPositionColor(positionData.global.position),
                        color: 'white',
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    Based on last {positionData.global.range_days} days (~9 months)
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Range High
                      </Typography>
                      <Typography variant="h6" fontWeight={600} color="error.main">
                        ${positionData.global.range_high.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Range Low
                      </Typography>
                      <Typography variant="h6" fontWeight={600} color="success.main">
                        ${positionData.global.range_low.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>

                  {renderRangeBar(positionData.global.position_pct, 'Position in Range')}

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Distance to High
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {positionData.global.distance_to_high_pct.toFixed(1)}%
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Distance to Low
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {positionData.global.distance_to_low_pct.toFixed(1)}%
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Market Comparison */}
          {positionData.nasdaq && positionData.relative_to_market && (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Market Comparison ({positionData.nasdaq.symbol})
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Relative Strength
                    </Typography>
                    <Chip
                      label={positionData.relative_to_market.relative_strength.replace('_', ' ')}
                      sx={{
                        mt: 1,
                        backgroundColor:
                          positionData.relative_to_market.relative_strength === 'outperforming'
                            ? theme.palette.success.main
                            : positionData.relative_to_market.relative_strength === 'underperforming'
                            ? theme.palette.error.main
                            : theme.palette.warning.main,
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Typography variant="body2" color="text.secondary">
                      Interpretation
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      {positionData.relative_to_market.interpretation}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  NASDAQ Position
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Local Position
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {positionData.nasdaq.local.position_label}
                      </Typography>
                      <Typography variant="caption">
                        {(positionData.nasdaq.local.position_pct * 100).toFixed(1)}% of range
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Global Position
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {positionData.nasdaq.global.position_label}
                      </Typography>
                      <Typography variant="caption">
                        {(positionData.nasdaq.global.position_pct * 100).toFixed(1)}% of range
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default MarketPosition;
